/**
 * Whether an organization has bought seats, read the one way it can be read.
 *
 * **Paying**, not *working*: an organization on a beta grant has active access,
 * a paid-through date and no subscription at all, so reading "has it bought
 * seats?" off its access tells it the purchase was already made — billed for
 * nought seats, a portal button that fails, and no way to buy. A subscription
 * is the only thing that says somebody is paying (0370/sale-overwrites-beta).
 */
import { isoDay } from './format';
import type { Me, Organization, SeatBilling } from './types';

export function hasBoughtSeats(billing: Pick<SeatBilling, 'stripeSubscription'> | null | undefined): boolean {
  return (billing?.stripeSubscription ?? null) !== null;
}

/** A failed charge or a grace week, said to whoever pays (`BillingBanner`). */
export interface Trouble {
  title: string;
  body: string;
  portal: string;
}

/** What the banner says, or `null` when there is nothing to say — pure, so it is tested here. */
export function billingTrouble(me: Me, organization: Organization | null, admin: boolean): Trouble | null {
  if (organization) {
    if (!admin || !hasBoughtSeats(organization.billing)) return null;
    const until = organization.billing.expiresAt;
    const portal = `/orgs/${encodeURIComponent(organization.id)}/billing/portal`;
    if (organization.billing.paymentFailedAt) {
      return {
        title: `The last payment for ${organization.name} did not go through`,
        body: `Stripe is trying the card again. Seats keep working${until ? ` until ${isoDay(until)}` : ''} — update the card, or confirm the payment, before then.`,
        portal
      };
    }
    if (organization.seatAccess === 'grace') {
      return {
        title: `${organization.name}’s seats are in their grace week`,
        body: `The period ended unpaid. Everybody keeps working${until ? ` until ${isoDay(until)}` : ''}, and then Airlock stops on their machines until the invoice is paid.`,
        portal
      };
    }
    return null;
  }

  const license = me.license;
  if (!license || license.orgId !== null || !license.stripeSubscription) return null;
  if (license.paymentFailedAt) {
    return {
      title: 'Your last payment did not go through',
      body: `Stripe is trying the card again. Airlock keeps working${license.expiresAt ? ` until ${isoDay(license.expiresAt)}` : ''} — update the card, or confirm the payment, before then.`,
      portal: '/billing/portal'
    };
  }
  if (me.access === 'grace') {
    return {
      title: 'Your licence is in its grace week',
      body: `The period ended unpaid. Airlock keeps working${license.expiresAt ? ` until ${isoDay(license.expiresAt)}` : ''}, and then stops until the invoice is paid.`,
      portal: '/billing/portal'
    };
  }
  return null;
}

/**
 * Whether an organization is on Enterprise now — the page's reading of the
 * Worker's `isEnterprise` (`tier.ts`): the tier, and seats that work. A lapsed
 * Enterprise organization is not, and neither is a trial or a grant. What this
 * decides is drawing; the Worker's 402 is what decides access.
 */
export function isEnterpriseOrg(organization: Pick<Organization, 'billing' | 'seatAccess'> | null | undefined): boolean {
  if (!organization || organization.billing.tier !== 'enterprise') return false;
  return organization.seatAccess === 'active' || organization.seatAccess === 'grace';
}

/** The plan word a trial carries (`TRIAL_PLAN` in the Worker's `env.ts`). */
const TRIAL = 'trial';

/** What the trial banner says (`TrialBanner`). */
export interface TrialNotice {
  title: string;
  body: string;
}

/**
 * The trial banner's words, or `null` when there is no trial to speak of —
 * pure, so it is tested here.
 *
 * Only to an owner or administrator (they are the ones who can buy), only
 * while the organization is on its trial and has bought nothing: the moment a
 * sale lands, `stripeSubscription` is set and the plan is a real one, and the
 * banner is gone on the next read of the organization. Said whether the trial
 * is running or has ended — the ended one is the one that most needs the
 * button.
 */
/**
 * Whole calendar days from today to the day `until` falls on, in UTC — what
 * « N days left » means beside « It runs to Oct 26 »: on Oct 19 that is 7,
 * where a ceiling over milliseconds said 8 for an end late in the day.
 */
export function daysLeft(until: number | null, now = Date.now()): number {
  if (until === null) return 0;
  const day = (ms: number): number => Math.floor(ms / 86_400_000);
  return Math.max(0, day(until) - day(now));
}

export function trialNotice(organization: Organization | null, admin: boolean, now = Date.now()): TrialNotice | null {
  if (!organization || !admin || hasBoughtSeats(organization.billing) || organization.billing.plan !== TRIAL) return null;
  const { paidThrough, expiresAt } = organization.billing;
  const left = paidThrough !== null && paidThrough > now ? Math.max(1, daysLeft(paidThrough, now)) : 0;
  if (left > 0) {
    return {
      title: `${organization.name} is on its trial — ${left} day${left === 1 ? '' : 's'} left`,
      body: `It runs to ${isoDay(paidThrough)}. Buying seats now keeps everybody on the same key; the seats given out carry over, and nobody is taken out.`
    };
  }
  return {
    title: `${organization.name}’s trial has ended`,
    body:
      expiresAt !== null && expiresAt > now
        ? `Seats keep working until ${isoDay(expiresAt)}. Buy seats to keep them — everybody stays on the same key.`
        : 'Airlock has stopped on its members’ machines. Buying seats starts it again on the same keys, without anybody reinstalling.'
  };
}

/**
 * The Overview's Billing card for an organization that pays for nothing,
 * said for what it is. A trial and a grant both have seats that work, and the
 * one sentence the card used to hold — Airlock on a machine "joins and pulls
 * once the seats are paid for" — told the owner of a fresh trial, under the
 * banner counting its fortnight down, that nobody's Airlock would work until
 * they paid.
 */
export function unpaidBillingNote(organization: Pick<Organization, 'billing'>, now = Date.now()): string {
  const { plan, paidThrough, expiresAt } = organization.billing;
  const running = paidThrough !== null && paidThrough > now;
  const grace = !running && expiresAt !== null && expiresAt > now;
  const what = plan === TRIAL ? 'trial' : plan === 'beta' ? 'grant from Airlock' : null;
  if (what && running) {
    return `On its ${what} until ${isoDay(paidThrough)}: the seats given out work and nothing is charged. Buying seats on Billing is what starts an invoice, and nobody’s key changes when it does.`;
  }
  if (what && grace) {
    return `Its ${what} has ended; the seats given out keep working until ${isoDay(expiresAt)}. Buying seats on Billing keeps them, on the same keys.`;
  }
  if (what) {
    return `Its ${what} has ended and Airlock has stopped on its members’ machines. Buying seats on Billing starts it again on the same keys.`;
  }
  return 'This organization buys nothing yet. Its pages work either way; what waits is Airlock on a machine, which works once a seat is bought on Billing and given out on Members.';
}

/**
 * What Members counts as bought and as free — the Worker's `freeSeats`, said
 * once on this side.
 *
 * Bought, not granted: an organization Airlock gave its seats to has none on
 * an invoice, and saying "0 seats bought" beside four people working is not
 * the truth. `free` is `null` where there is no ceiling to count against: an
 * organization that was given its seats has no quantity, and a number would
 * be a limit it does not have.
 *
 * A trial is not a grant: it has a ceiling, the trial's seats, counted against
 * whether or not the fortnight has run out (as `freeSeats` does). The page used
 * to file it with the grants, and the owner of a fresh trial read "granted"
 * and no seat free beside a banner offering five.
 */
export function seatFigures(
  organization: Pick<Organization, 'billing' | 'seatAccess'> | null,
  holding: number,
  trialSeats: number
): { bought: string; free: number | null } {
  const billing = organization?.billing ?? null;
  if (hasBoughtSeats(billing)) {
    const paid = billing?.seatsPaid ?? 0;
    return { bought: String(paid), free: Math.max(0, paid - holding) };
  }
  if (billing?.plan === TRIAL) return { bought: `trial · ${trialSeats}`, free: Math.max(0, trialSeats - holding) };
  if ((organization?.seatAccess ?? 'none') !== 'none') return { bought: 'granted', free: null };
  return { bought: 'none', free: 0 };
}
